import { useEffect, useState } from "react";
import { ActionByType } from "../../common/ProfileDocument";
import {
  TrainingDocument,
  TrainingDocumentOperationType,
} from "../../common/TrainingDocument";
import {
  useDeleteTrainingDocumentMutation,
  useDownloadTrainingDocumentMutation,
  useGetProfileTrainingDocumentsQuery,
} from "../../redux/api/trainingDocument";
import convertDateToString from "../../util/ConvertDateToString";
import {
  Button,
  Dialog,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { formatDateString } from "../../common/FormatDateString";
import {
  Delete,
  FileOpen,
  Visibility,
  VisibilityOff,
} from "@mui/icons-material";
import TrainingDocumentUploadModal from "./TrainingDocumentUploadModal";
import TrainingDocumentUpdateModal from "./TrainingDocumentUpdateModal";

interface ProfileTrainingDocumentsProps {
  profileId: number;
  isViewOnly: boolean;
  parentCallback: Function;
  actionByType: ActionByType;
}

const ProfileTrainingDocuments = (props: ProfileTrainingDocumentsProps) => {
  const [showExpiredDocuments, setShowExpiredDocuments] = useState(true);
  const [trainingDocuments, setTrainingDocuments] = useState<
    TrainingDocument[]
  >([]);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [currDocId, setCurrDocId] = useState<number>();

  const {
    data: documentsFromApi,
    isLoading,
    isFetching,
    refetch,
  } = useGetProfileTrainingDocumentsQuery(props.profileId);
  const [deleteTrainingDocument] = useDeleteTrainingDocumentMutation();
  const [downloadTrainingDocument] = useDownloadTrainingDocumentMutation();

  useEffect(() => {
    if (!isFetching && !isLoading) {
      let res: any = JSON.parse(JSON.stringify(documentsFromApi));
      setTrainingDocuments(
        res
          .filter(
            (document: any) =>
              showExpiredDocuments ||
              document.lifetimeValidity ||
              (document["expirationDate"] !== undefined &&
                new Date(document["expirationDate"]) > new Date())
          )
          .map((doc: any) => {
            Object.keys(doc).forEach(
              (key) => (doc[key] = doc[key] === null ? "" : doc[key])
            );
            if (doc["issueDate"] !== undefined && doc["issueDate"] !== "") {
              doc["issueDate"] = convertDateToString(
                new Date(doc["issueDate"])
              );
            }
            if (
              doc["expirationDate"] !== undefined &&
              doc["expirationDate"] !== ""
            ) {
              doc["expirationDate"] = convertDateToString(
                new Date(doc["expirationDate"])
              );
            }
            return doc as TrainingDocument;
          })
      );
    }
  }, [documentsFromApi, isFetching, isLoading, showExpiredDocuments]);

  const handleUpload = () => {
    refetch();
    props.parentCallback(TrainingDocumentOperationType.UPLOAD);
  };

  const deleteDocument = async () => {
    if (currDocId === undefined) {
      return;
    }
    const response = await deleteTrainingDocument({
      trainingDocumentId: currDocId.toString(),
      actionByType: props.actionByType,
    });
    if ("error" in response) {
      // TODO: error handling/surfacing
    }
    refetch();
    props.parentCallback(TrainingDocumentOperationType.DELETE);
    handleClose();
  };

  // const handleDownload = async (url: string, fileName: string) => {
  //   const response = await downloadTrainingDocument({
  //     fileKey: url,
  //   });
  //   if ("error" in response === false) {
  //     // create a temporary URL and link and simulate a click to trigger the browser download behavior
  //     // @ts-ignore - if there is no error object in the response, we know it must have a Blob 'data' attribute
  //     const url = window.URL.createObjectURL(response.data);
  //     const link = document.createElement("a");
  //     link.href = url;
  //     link.setAttribute("download", fileName);
  //     document.body.appendChild(link);
  //     link.click();
  //     document.body.removeChild(link);

  //     props.parentCallback(TrainingDocumentOperationType.DOWNLOAD);
  //   }
  // };

  const handleView = async (url: string, contentType: string, fileName: string) => {
    const response = await downloadTrainingDocument({
      fileKey: url,
    });
    if ("error" in response === false) {


      // @ts-ignore - if there is no error object in the response, we know it must have a Blob 'data' attribute
      const url = window.URL.createObjectURL(new Blob([response.data], { type: contentType }));
      const inlineTypes = [
        'application/pdf',
        'image/png',
        'image/jpeg',
      ];

      if (inlineTypes.includes(contentType)) {
        window.open(url, "_blank");
      } else {
        const link = document.createElement("a");
        
        link.href = url;
        link.target = "_blank"
        link.download = fileName;

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    }
  };

  const handleOpen = (documentId: number) => {
    setCurrDocId(documentId);
    setConfirmDelete(true);
  };

  const handleClose = () => {
    setConfirmDelete(false);
  };

  const handleUpdate = () => {
    refetch();
    props.parentCallback(TrainingDocumentOperationType.EDIT);
  };

  return (
    <>
      <TableContainer>
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <div>
            <h3 className="mb-0">Training Documents</h3>
          </div>
          <div>
            <Button
              className="grey-link-button"
              onClick={() => setShowExpiredDocuments(!showExpiredDocuments)}
            >
              {showExpiredDocuments ? <Visibility /> : <VisibilityOff />}
              Show Expired Documents
            </Button>
          </div>
        </div>
        <Table className="documents-table">
          <TableHead>
            <TableRow>
              <TableCell style={{ width: "25%" }}>TRAINING</TableCell>
              <TableCell>FILE NAME</TableCell>
              <TableCell>ISSUE DATE</TableCell>
              <TableCell>EXPIRATION DATE</TableCell>
              {!props.isViewOnly && <TableCell />}
              {/* empty cell for icons */}
            </TableRow>
          </TableHead>
          <TableBody>
            {trainingDocuments.map((doc) => (
              <TableRow key={doc.id}>
                <TableCell>{doc.training?.name}</TableCell>
                <TableCell>{doc.displayName}</TableCell>
                <TableCell>{formatDateString(doc.issueDate)}</TableCell>
                <TableCell>{formatDateString(doc.expirationDate)}</TableCell>
                {!props.isViewOnly && (
                  <TableCell className="document-actions">
                    <Button
                      className="blue-link-button"
                      onClick={() =>
                        handleView(doc.fileUrl, doc.contentType, doc.displayName)
                      }
                    >
                      <FileOpen />
                      View
                    </Button>
                    <TrainingDocumentUpdateModal
                      document={doc}
                      onUpdate={handleUpdate}
                      actionByType={props.actionByType}
                    />
                    <Button
                      className="blue-link-button"
                      onClick={() => handleOpen(doc.id)}
                    >
                      <Delete />
                      Delete
                    </Button>
                    {/* <Button
                      className="blue-link-button"
                      onClick={() =>
                        handleDownload(doc.fileUrl, doc.displayName)
                      }
                    >
                      <CloudDownload />
                      Download
                    </Button> */}
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {!props.isViewOnly && (
        <TrainingDocumentUploadModal
          profileId={props.profileId}
          onUpload={handleUpload}
          actionByType={props.actionByType}
        />
      )}
      <Dialog
        open={confirmDelete}
        onClose={handleClose}
        aria-labelledby="modal-title"
      >
        <Typography variant="h3" id="modal-title">
          Delete Document
        </Typography>
        <p>Are you sure you would like to delete this document?</p>
        <div className="modal-footer">
          <Button className="outline-button-small" onClick={deleteDocument}>
            Confirm
          </Button>
          <Button className="blue-button-small" onClick={handleClose}>
            Cancel
          </Button>
        </div>
      </Dialog>
    </>
  );
};

export default ProfileTrainingDocuments;
